import { useQuery } from '@tanstack/react-query';
import { useMsal } from '@azure/msal-react';
import { useAuthenticatedFetch } from './useAuthenticatedFetch';
import { usePublicFetch } from './usePublicFetch';
import { useOTPAuth } from '../auth/providers/OTPAuthProvider';
import { API_URL } from '../utils/config';

// Data interfaces
export interface Continent {
  id: string;
  name: string;
  code: string;
  isActive?: boolean;
  countryCount: number;
  activeCountryCount: number;
  marketCount: number;
  activeMarketCount: number;
}

export interface Country {
  id: string;
  name: string;
  code: string;
  continentId: string;
  marketCount: number;
  activeMarketCount: number;
  isActive?: boolean;
}

export interface Market {
  id: string;
  name: string;
  countryId: string;
  isActive: boolean;
  slug: string;
  productImage: string;
  productImage2?: string;
  productImage3?: string;
  sortOrder: number;
  subscription?: {
    productId: string;
    productName: string;
    description: string;
    prices: Array<{
      priceId: string;
      amount: number;
      currency: string;
      interval: 'month' | 'year';
    }>;
  };
}

export interface MarketSearchResult extends Market {
  country: {
    id: string;
    name: string;
    code: string;
  };
  continent: {
    id: string;
    name: string;
  };
  relevanceScore: number;
}

export interface MarketDetails extends Market {
  description: string;
  reportSections: Array<{
    category: string;
    categoryDescription?: string;
    sections: Array<{
      title: string;
      description: string;
    }>;
  }>;
  country: {
    id: string;
    name: string;
    code: string;
  };
  continent: {
    id: string;
    name: string;
    code: string;
  };
  relatedMarkets: Array<{
    id: string;
    name: string;
    slug: string;
    relationType: string;
  }>;
  submarkets?: Array<{
    id: number;
    name: string;
  }>;
  propertyClassDistribution?: Array<{
    class: string;
    count: number;
  }>;
  subscription?: {
    productId: string;
    productName: string;
    description: string;
    prices: Array<{
      priceId: string;
      amount: number;
      currency: string;
      interval: 'month' | 'year';
    }>;
  };
  singlePurchase?: {
    productId: string;
    productName: string;
    description: string;
    prices: Array<{
      priceId: string;
      amount: number;
      currency: string;
    }>;
  };
}

// API Response wrapper
interface ApiResponse<T> {
  success: boolean;
  data: T;
  metadata?: any;
}

// Custom hooks for MarketCompass API
export const useContinents = () => {
  const { accounts } = useMsal();
  const { isAuthenticated: isOTPAuthenticated } = useOTPAuth();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const { publicFetch } = usePublicFetch();
  const isAuthenticated = accounts.length > 0 || isOTPAuthenticated;

  return useQuery<Continent[], Error>({
    queryKey: ['continents'],
    queryFn: async (): Promise<Continent[]> => {
      try {
        const url = `${API_URL}/api/store/markets/continents`;
        // Use authenticated fetch if logged in, otherwise use public fetch
        const response = isAuthenticated 
          ? await authenticatedFetch(url)
          : await publicFetch(url);
        
        if (!response) {
          console.error('Failed to fetch continents: No response');
          return [];
        }
        
        if (!response.ok) {
          console.error(`Failed to fetch continents: HTTP ${response.status} ${response.statusText}`);
          return [];
        }

        const result: ApiResponse<Continent[]> = await response.json();
        return result.data || [];
      } catch (error) {
        console.error('Continents fetch error:', error);
        throw error;
      }
    },
    enabled: true, // Always enabled now, works for both authenticated and unauthenticated
    staleTime: 10 * 60 * 1000, // Consider continents data fresh for 10 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    placeholderData: (previousData) => previousData, // Keep showing previous data while fetching
  });
};

export const useCountriesByContinent = (continentId: string | undefined) => {
  const { accounts } = useMsal();
  const { isAuthenticated: isOTPAuthenticated } = useOTPAuth();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const { publicFetch } = usePublicFetch();
  const isAuthenticated = accounts.length > 0 || isOTPAuthenticated;

  return useQuery<Country[], Error>({
    queryKey: ['countries', continentId],
    queryFn: async (): Promise<Country[]> => {
      try {
        if (!continentId) {
          return [];
        }

        const url = `${API_URL}/api/store/markets/countries/${continentId}`;
        // Use authenticated fetch if logged in, otherwise use public fetch
        const response = isAuthenticated 
          ? await authenticatedFetch(url)
          : await publicFetch(url);
        
        if (!response) {
          console.error('Failed to fetch countries: No response');
          return [];
        }
        
        if (!response.ok) {
          console.error(`Failed to fetch countries: HTTP ${response.status} ${response.statusText}`);
          return [];
        }

        const result: ApiResponse<Country[]> = await response.json();
        return result.data || [];
      } catch (error) {
        console.error('Countries fetch error:', error);
        throw error;
      }
    },
    enabled: !!continentId, // Enable for both authenticated and unauthenticated users
    staleTime: 10 * 60 * 1000, // Consider countries data fresh for 10 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
  });
};

export const useMarketsByCountry = (countryId: string | undefined) => {
  const { accounts } = useMsal();
  const { isAuthenticated: isOTPAuthenticated } = useOTPAuth();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const { publicFetch } = usePublicFetch();
  const isAuthenticated = accounts.length > 0 || isOTPAuthenticated;

  return useQuery<Market[], Error>({
    queryKey: ['markets', countryId],
    queryFn: async (): Promise<Market[]> => {
      try {
        if (!countryId) {
          return [];
        }

        const url = `${API_URL}/api/store/markets/country/${countryId}`;
        // Use authenticated fetch if logged in, otherwise use public fetch
        const response = isAuthenticated
          ? await authenticatedFetch(url)
          : await publicFetch(url);

        if (!response) {
          console.error('Failed to fetch markets: No response');
          return [];
        }

        if (!response.ok) {
          console.error(`Failed to fetch markets: HTTP ${response.status} ${response.statusText}`);
          return [];
        }

        const result: ApiResponse<Market[]> = await response.json();
        return result.data || [];
      } catch (error) {
        console.error('Markets fetch error:', error);
        throw error;
      }
    },
    enabled: !!countryId, // Enable for both authenticated and unauthenticated users
    staleTime: 5 * 60 * 1000, // Consider markets data fresh for 5 minutes
    gcTime: 15 * 60 * 1000, // Keep in cache for 15 minutes
  });
};

// Response type for markets by country slug endpoint (includes metadata)
export interface MarketsByCountrySlugResponse {
  markets: Market[];
  country: {
    id: string;
    name: string;
    code: string;
    isActive: boolean;
    marketCount: number;
    activeMarketCount: number;
  };
  continent: {
    id: string;
    name: string;
    code: string;
  };
}

export const useMarketsByCountrySlug = (continentSlug?: string, countrySlug?: string) => {
  const { accounts } = useMsal();
  const { isAuthenticated: isOTPAuthenticated } = useOTPAuth();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const { publicFetch } = usePublicFetch();
  const isAuthenticated = accounts.length > 0 || isOTPAuthenticated;

  return useQuery<MarketsByCountrySlugResponse | null, Error>({
    queryKey: ['marketsByCountrySlug', continentSlug, countrySlug],
    queryFn: async (): Promise<MarketsByCountrySlugResponse | null> => {
      try {
        if (!continentSlug || !countrySlug) {
          return null;
        }

        const url = `${API_URL}/api/store/markets/by-path/${continentSlug}/${countrySlug}`;
        // Use authenticated fetch if logged in, otherwise use public fetch
        const response = isAuthenticated
          ? await authenticatedFetch(url)
          : await publicFetch(url);

        if (!response) {
          console.error('Failed to fetch markets by country slug: No response');
          return null;
        }

        if (!response.ok) {
          console.error(`Failed to fetch markets by country slug: HTTP ${response.status} ${response.statusText}`);
          return null;
        }

        const result = await response.json();

        if (!result.success || !result.data || !result.metadata) {
          return null;
        }

        return {
          markets: result.data,
          country: result.metadata.country,
          continent: result.metadata.continent
        };
      } catch (error) {
        console.error('Markets by country slug fetch error:', error);
        throw error;
      }
    },
    enabled: !!continentSlug && !!countrySlug,
    staleTime: 5 * 60 * 1000, // Consider markets data fresh for 5 minutes
    gcTime: 15 * 60 * 1000, // Keep in cache for 15 minutes
    retry: 1, // Only retry once to prevent rapid repeated requests
    retryDelay: 1000, // Wait 1 second before retrying
    placeholderData: (previousData) => previousData, // Keep showing previous data while re-enabling query
  });
};

export const useMarketSearch = (query: string, options?: { limit?: number; category?: string }) => {
  const { accounts } = useMsal();
  const { isAuthenticated: isOTPAuthenticated } = useOTPAuth();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const { publicFetch } = usePublicFetch();
  const isAuthenticated = accounts.length > 0 || isOTPAuthenticated;

  return useQuery<MarketSearchResult[], Error>({
    queryKey: ['marketSearch', query, options],
    queryFn: async (): Promise<MarketSearchResult[]> => {
      try {
        if (!query || query.trim().length < 2) {
          return [];
        }

        const params = new URLSearchParams();
        params.append('query', query.trim());
        if (options?.limit) params.append('limit', options.limit.toString());
        if (options?.category) params.append('category', options.category);

        const url = `${API_URL}/api/store/markets/search?${params.toString()}`;
        // Use authenticated fetch if logged in, otherwise use public fetch
        const response = isAuthenticated 
          ? await authenticatedFetch(url)
          : await publicFetch(url);
        
        if (!response) {
          console.error('Failed to search markets: No response');
          return [];
        }
        
        if (!response.ok) {
          console.error(`Failed to search markets: HTTP ${response.status} ${response.statusText}`);
          return [];
        }

        const result: ApiResponse<MarketSearchResult[]> = await response.json();
        return result.data || [];
      } catch (error) {
        console.error('Market search error:', error);
        throw error;
      }
    },
    enabled: !!query && query.trim().length >= 2, // Enable for both authenticated and unauthenticated users
    staleTime: 2 * 60 * 1000, // Consider search results fresh for 2 minutes
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });
};

export const useMarketBySlug = (continentSlug?: string, countrySlug?: string, marketSlug?: string) => {
  const { accounts } = useMsal();
  const { isAuthenticated: isOTPAuthenticated } = useOTPAuth();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const { publicFetch } = usePublicFetch();
  const isAuthenticated = accounts.length > 0 || isOTPAuthenticated;

  return useQuery<MarketDetails | null, Error>({
    queryKey: ['marketBySlug', continentSlug, countrySlug, marketSlug],
    queryFn: async (): Promise<MarketDetails | null> => {
      try {
        if (!continentSlug || !countrySlug || !marketSlug) {
          return null;
        }

        const url = `${API_URL}/api/store/markets/by-slug/${continentSlug}/${countrySlug}/${marketSlug}`;
        // Use authenticated fetch if logged in, otherwise use public fetch
        const response = isAuthenticated 
          ? await authenticatedFetch(url)
          : await publicFetch(url);
        
        if (!response) {
          console.error('Failed to fetch market by slug: No response');
          return null;
        }
        
        if (!response.ok) {
          console.error(`Failed to fetch market by slug: HTTP ${response.status} ${response.statusText}`);

          // For 401 Unauthorized or 403 Forbidden, throw a specific error to help with user experience
          if (response.status === 401 || response.status === 403) {
            const errorData = await response.json().catch(() => ({ message: 'Unauthorized access' }));
            const error = new Error(errorData.message || 'Unauthorized access to this market');
            (error as any).status = response.status;
            (error as any).isUnauthorized = true;
            throw error;
          }

          return null;
        }

        const result: ApiResponse<MarketDetails> = await response.json();
        return result.data || null;
      } catch (error) {
        console.error('Market by slug fetch error:', error);
        throw error;
      }
    },
    enabled: !!continentSlug && !!countrySlug && !!marketSlug, // Enable for both authenticated and unauthenticated users
    retry: (failureCount, error) => {
      // Don't retry on 401/403 errors - these are expected authorization failures
      const status = (error as any)?.status;
      if (status === 401 || status === 403 || (error as any)?.isUnauthorized) {
        return false;
      }
      // Use default retry behavior for other errors (respects global retry config)
      return failureCount < 1;
    },
    staleTime: 5 * 60 * 1000, // Consider market data fresh for 5 minutes
    gcTime: 15 * 60 * 1000, // Keep in cache for 15 minutes
  });
};

export const useMarketDetails = (marketId: string | undefined) => {
  const { accounts } = useMsal();
  const { isAuthenticated: isOTPAuthenticated } = useOTPAuth();
  const { authenticatedFetch } = useAuthenticatedFetch();

  const isAuthenticated = accounts.length > 0 || isOTPAuthenticated;

  return useQuery<MarketDetails, Error>({
    queryKey: ['marketDetails', marketId],
    queryFn: async (): Promise<MarketDetails> => {
      try {
        if (!isAuthenticated || !marketId) {
          throw new Error('No authenticated user or market ID');
        }

        const url = `${API_URL}/api/marketcompass/markets/${marketId}/details`;
        const response = await authenticatedFetch(url);

        if (!response) {
          throw new Error('Failed to fetch market details: No response from authenticatedFetch');
        }

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to fetch market details: HTTP ${response.status} - ${errorText}`);
        }

        const result: ApiResponse<MarketDetails> = await response.json();
        if (!result.data) {
          throw new Error('No market details data received');
        }

        return result.data;
      } catch (error) {
        console.error('Market details fetch error:', error);
        throw error;
      }
    },
    enabled: isAuthenticated && !!marketId,
    staleTime: 5 * 60 * 1000, // Consider market details fresh for 5 minutes
    gcTime: 15 * 60 * 1000, // Keep in cache for 15 minutes
  });
};

// Market Grouping interfaces
export interface MarketGrouping {
  id: number;
  name: string;
  description: string;
  product_image_url?: string;  // URL to the grouping's product image
  market_count: number;  // API returns snake_case
}

export interface MarketGroupingDetails {
  id: number;
  name: string;
  description: string;
  markets: Array<{
    id: number;
    name: string;
    display_name?: string;
    slug: string;
    description?: string;
    product_image?: string;
    product_image2?: string;
    isActive: boolean;
    country: {
      id: string;
      name: string;
      slug: string;
      code: string;
    };
    continent: {
      id: string;
      name: string;
      slug: string;
    };
  }>;
}

// Hook to get user's market groupings
export const useUserMarketGroupings = () => {
  const { accounts } = useMsal();
  const { isAuthenticated: isOTPAuthenticated } = useOTPAuth();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const isAuthenticated = accounts.length > 0 || isOTPAuthenticated;

  return useQuery<MarketGrouping[], Error>({
    queryKey: ['userMarketGroupings'],
    queryFn: async (): Promise<MarketGrouping[]> => {
      try {
        if (!isAuthenticated) {
          return [];
        }

        const url = `${API_URL}/api/store/market-groupings`;
        const response = await authenticatedFetch(url);

        if (!response) {
          console.error('Failed to fetch market groupings: No response');
          return [];
        }

        if (!response.ok) {
          console.error(`Failed to fetch market groupings: HTTP ${response.status} ${response.statusText}`);
          return [];
        }

        const result: any = await response.json();
        return result.groupings || [];
      } catch (error) {
        console.error('Market groupings fetch error:', error);
        throw error;
      }
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // Consider groupings data fresh for 5 minutes
    gcTime: 15 * 60 * 1000, // Keep in cache for 15 minutes
  });
};

// Hook to get market grouping details by ID
export const useMarketGroupingDetails = (groupingId: number | undefined) => {
  const { accounts } = useMsal();
  const { isAuthenticated: isOTPAuthenticated } = useOTPAuth();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const isAuthenticated = accounts.length > 0 || isOTPAuthenticated;

  return useQuery<MarketGroupingDetails | null, Error>({
    queryKey: ['marketGroupingDetails', groupingId],
    queryFn: async (): Promise<MarketGroupingDetails | null> => {
      try {
        if (!isAuthenticated || !groupingId) {
          return null;
        }

        const url = `${API_URL}/api/store/market-groupings/${groupingId}`;
        const response = await authenticatedFetch(url);

        if (!response) {
          console.error('Failed to fetch grouping details: No response');
          return null;
        }

        if (!response.ok) {
          console.error(`Failed to fetch grouping details: HTTP ${response.status} ${response.statusText}`);
          return null;
        }

        const result: any = await response.json();
        return result.grouping || null;
      } catch (error) {
        console.error('Market grouping details fetch error:', error);
        throw error;
      }
    },
    enabled: isAuthenticated && !!groupingId,
    staleTime: 5 * 60 * 1000, // Consider grouping details fresh for 5 minutes
    gcTime: 15 * 60 * 1000, // Keep in cache for 15 minutes
  });
};

export interface PropertySearchResult {
  id: number;
  propertyName: string;
  city: string | null;
  rooms: number | null;
  marketName: string | null;
  submarketName: string | null;
  marketSlug: string | null;
}

export const usePropertySearchByCountry = (
  continentSlug: string | undefined,
  countrySlug: string | undefined,
  query: string,
  options?: { limit?: number }
) => {
  const { accounts } = useMsal();
  const { isAuthenticated: isOTPAuthenticated } = useOTPAuth();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const { publicFetch } = usePublicFetch();
  const isAuthenticated = accounts.length > 0 || isOTPAuthenticated;

  return useQuery<PropertySearchResult[], Error>({
    queryKey: ['propertySearchByCountry', continentSlug, countrySlug, query, options],
    queryFn: async (): Promise<PropertySearchResult[]> => {
      try {
        if (!continentSlug || !countrySlug || !query || query.trim().length < 2) {
          return [];
        }

        const params = new URLSearchParams();
        params.append('query', query.trim());
        if (options?.limit) params.append('limit', options.limit.toString());

        const url = `${API_URL}/api/store/markets/by-path/${continentSlug}/${countrySlug}/property-search?${params.toString()}`;
        // Use authenticated fetch if logged in, otherwise use public fetch
        const response = isAuthenticated
          ? await authenticatedFetch(url)
          : await publicFetch(url);

        if (!response) {
          console.error('Failed to search properties: No response');
          return [];
        }

        if (!response.ok) {
          console.error(`Failed to search properties: HTTP ${response.status} ${response.statusText}`);
          return [];
        }

        const result: ApiResponse<PropertySearchResult[]> = await response.json();
        return result.data || [];
      } catch (error) {
        console.error('Property search error:', error);
        throw error;
      }
    },
    enabled: !!continentSlug && !!countrySlug && !!query && query.trim().length >= 2,
    staleTime: 2 * 60 * 1000, // Consider search results fresh for 2 minutes
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    placeholderData: (previousData) => previousData,
  });
};